import React from 'react';
import {
  isNativeClerkPlatform,
  NativeClerk,
  type NativeClerkOAuthProvider,
  type NativeClerkSnapshot,
  type NativeClerkUpdateUserOptions,
  type NativeClerkUser,
} from './nativeClerkPlugin';

type NativeUser = NativeClerkUser & {
  update: (params: NativeClerkUpdateUserOptions) => Promise<NativeUser>;
  reload: () => Promise<NativeUser | null>;
};

interface NativeAuthContextValue {
  isNativePlatform: boolean;
  isAvailable: boolean;
  unavailableReason: string | null;
  isLoaded: boolean;
  isSignedIn: boolean;
  user: NativeUser | null;
  signInWithOAuth: (provider: NativeClerkOAuthProvider) => Promise<NativeClerkSnapshot>;
  signInWithPassword: (identifier: string, password: string) => Promise<NativeClerkSnapshot>;
  signOut: () => Promise<NativeClerkSnapshot>;
  getToken: (options?: { template?: string; skipCache?: boolean }) => Promise<string | null>;
  updateUser: (params: NativeClerkUpdateUserOptions) => Promise<NativeUser>;
  reloadUser: () => Promise<NativeUser | null>;
  openUserProfile: () => Promise<void>;
}

const NativeAuthContext = React.createContext<NativeAuthContextValue | null>(null);

const unloadedSnapshot: NativeClerkSnapshot = {
  isLoaded: false,
  isSignedIn: false,
  user: null,
};

const nativeErrorMessage = (err: unknown): string =>
  (err as { errors?: Array<{ longMessage?: string; message?: string }> })?.errors?.[0]
    ?.longMessage ||
  (err as { errors?: Array<{ message?: string }> })?.errors?.[0]?.message ||
  (err as { message?: string })?.message ||
  'Native Clerk could not be configured.';

export const NativeAuthProvider: React.FC<{
  publishableKey: string;
  children?: React.ReactNode;
}> = ({ publishableKey, children }) => {
  const nativePlatform = isNativeClerkPlatform();
  const canTryNative = nativePlatform && !!publishableKey;
  const [nativeUnavailable, setNativeUnavailable] = React.useState(false);
  const [unavailableReason, setUnavailableReason] = React.useState<string | null>(
    nativePlatform && !publishableKey ? 'Missing Clerk publishable key for this native build.' : null
  );
  const isAvailable = canTryNative && !nativeUnavailable;
  const [snapshot, setSnapshot] = React.useState<NativeClerkSnapshot>(
    canTryNative ? unloadedSnapshot : { ...unloadedSnapshot, isLoaded: true }
  );

  const applySnapshot = React.useCallback((next: NativeClerkSnapshot): NativeClerkSnapshot => {
    setSnapshot(next);
    return next;
  }, []);

  React.useEffect(() => {
    setNativeUnavailable(false);
    setUnavailableReason(
      nativePlatform && !publishableKey ? 'Missing Clerk publishable key for this native build.' : null
    );

    if (!canTryNative) {
      setSnapshot({ ...unloadedSnapshot, isLoaded: true });
      return;
    }

    let cancelled = false;
    setSnapshot(unloadedSnapshot);
    void (async () => {
      try {
        const next = await NativeClerk.configure({ publishableKey });
        if (!cancelled) applySnapshot(next);
      } catch (err) {
        if (!cancelled) {
          setNativeUnavailable(true);
          setUnavailableReason(nativeErrorMessage(err));
          setSnapshot({ ...unloadedSnapshot, isLoaded: true });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [applySnapshot, canTryNative, nativePlatform, publishableKey]);

  const makeUser = React.useCallback(
    (user: NativeClerkUser | null): NativeUser | null => {
      if (!user) return null;
      return {
        ...user,
        update: async (params) => {
          const next = await NativeClerk.updateUser(params);
          applySnapshot(next);
          const updated = makeUser(next.user);
          if (!updated) throw new Error('No signed-in user after profile update.');
          return updated;
        },
        reload: async () => {
          const next = await NativeClerk.reloadUser();
          applySnapshot(next);
          return makeUser(next.user);
        },
      };
    },
    [applySnapshot]
  );

  const value = React.useMemo<NativeAuthContextValue>(
    () => ({
      isNativePlatform: nativePlatform,
      isAvailable,
      unavailableReason,
      isLoaded: snapshot.isLoaded,
      isSignedIn: snapshot.isSignedIn,
      user: makeUser(snapshot.user),
      signInWithOAuth: async (provider) => {
        const next = await NativeClerk.signInWithOAuth({ provider });
        return applySnapshot(next);
      },
      signInWithPassword: async (identifier, password) => {
        const next = await NativeClerk.signInWithPassword({ identifier, password });
        return applySnapshot(next);
      },
      signOut: async () => {
        const next = await NativeClerk.signOut();
        return applySnapshot(next);
      },
      getToken: async (options) => {
        const { token } = await NativeClerk.getToken(options);
        return token;
      },
      updateUser: async (params) => {
        const next = await NativeClerk.updateUser(params);
        applySnapshot(next);
        const updated = makeUser(next.user);
        if (!updated) throw new Error('No signed-in user after profile update.');
        return updated;
      },
      reloadUser: async () => {
        const next = await NativeClerk.reloadUser();
        applySnapshot(next);
        return makeUser(next.user);
      },
      openUserProfile: async () => {
        await NativeClerk.openUserProfile();
      },
    }),
    [
      applySnapshot,
      isAvailable,
      makeUser,
      nativePlatform,
      snapshot.isLoaded,
      snapshot.isSignedIn,
      snapshot.user,
      unavailableReason,
    ]
  );

  return <NativeAuthContext.Provider value={value}>{children}</NativeAuthContext.Provider>;
};

export const useNativeAuthOptional = (): NativeAuthContextValue | null =>
  React.useContext(NativeAuthContext);

export const useNativeAuth = (): NativeAuthContextValue => {
  const value = useNativeAuthOptional();
  if (!value) {
    throw new Error('useNativeAuth must be used inside NativeAuthProvider.');
  }
  return value;
};
